import cn from 'classnames'
import {FC, ReactNode, useCallback, useRef, useState} from 'react'

import {theme} from '@/theme/theme'

import {Text, TextVariant} from '../../content/Text'

const TOOLTIP_ARROW_WIDTH = 10

type IconTooltipProps = {
  onClick?: (e?: any) => void
  icon?: ReactNode
  tooltipText: string
  backgroundColor?: string
  maxWidth?: string
}

type IconTooltipPosition = 'left' | 'right' | 'center'

export const IconTooltip: FC<React.PropsWithChildren<IconTooltipProps>> = ({
  onClick,
  icon,
  tooltipText,
  backgroundColor = theme.palette.tertiary.main,
  maxWidth,
  children,
}) => {
  const [position, setPosition] = useState<IconTooltipPosition>('center')
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const adjustTooltipPosition = useCallback(() => {
    if (!tooltipRef.current || !wrapperRef.current) return
    const tooltip = tooltipRef.current

    // Measure unconstrained natural content width
    tooltip.style.width = 'max-content'
    const naturalWidth = tooltip.getBoundingClientRect().width

    if (naturalWidth === 0) {
      // Tooltip is hidden (mouseleave) — reset all overrides
      tooltip.style.width = ''
      tooltip.style.left = ''
      tooltip.style.right = ''
      setPosition('center')
      return
    }

    // Cap at maxWidth and lock the width so overflow math is stable
    const maxWidthPx = maxWidth ? parseFloat(maxWidth) : Infinity
    tooltip.style.width = isFinite(maxWidthPx) ? `${Math.min(naturalWidth, maxWidthPx)}px` : ''

    const tooltipRect = tooltip.getBoundingClientRect()
    const wrapperRect = wrapperRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    let newPosition: IconTooltipPosition = 'center'
    if (tooltipRect.right > viewportWidth) {
      newPosition = 'right'
    } else if (tooltipRect.left < 0) {
      newPosition = 'left'
    }

    setPosition(newPosition)

    tooltip.style.left =
      newPosition === 'left' ? `${wrapperRect.width / 2 - TOOLTIP_ARROW_WIDTH * 2}px` : ''
    tooltip.style.right =
      newPosition === 'right' ? `${wrapperRect.width / 2 - TOOLTIP_ARROW_WIDTH * 2}px` : ''
  }, [maxWidth])

  return (
    <>
      <div
        className="wrapper"
        ref={wrapperRef}
        onMouseEnter={adjustTooltipPosition}
        onMouseLeave={adjustTooltipPosition}
      >
        {icon ? (
          <button
            onClick={onClick}
            onFocus={adjustTooltipPosition}
            type="button"
            className="trigger"
            aria-label={tooltipText}
          >
            {icon}
          </button>
        ) : (
          children
        )}
        <div className={cn('tooltip', position)} ref={tooltipRef}>
          <div className="tooltip-text">
            <Text variant={TextVariant.caption} element="p" color={theme.palette.text.white}>
              {tooltipText}
            </Text>
          </div>
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .trigger {
          display: flex;
          padding: ${theme.spacing[1]};
          margin: -${theme.spacing[1]};
        }
        .tooltip {
          display: none;
        }
        .wrapper:hover .tooltip,
        .trigger:focus-visible ~ .tooltip {
          display: flex;
          justify-content: center;
          white-space: ${maxWidth ? 'normal' : 'nowrap'};
          margin-bottom: ${theme.spacing[2.5]};
          position: absolute;
          bottom: 100%;
          z-index: 1;
        }
        @media (hover: none) {
          .wrapper:hover .tooltip {
            display: none;
          }
        }
        .tooltip.center {
          left: 50%;
          transform: translateX(-50%);
        }
        .tooltip-text::after {
          content: '';
          position: absolute;
          bottom: -15px;
          border-width: ${TOOLTIP_ARROW_WIDTH}px;
          border-style: solid;
          border-color: ${backgroundColor} transparent transparent transparent;
          pointer-events: none;
        }
        .tooltip-text {
          background-color: ${backgroundColor};
          padding: ${theme.spacing[2]};
          border-radius: 8px;
        }
        .tooltip.center .tooltip-text::after {
          left: calc(50% - ${TOOLTIP_ARROW_WIDTH}px);
        }
        .tooltip.right .tooltip-text::after {
          right: ${TOOLTIP_ARROW_WIDTH}px;
        }
        .tooltip.left .tooltip-text::after {
          left: ${TOOLTIP_ARROW_WIDTH}px;
        }
      `}</style>
    </>
  )
}
